import { CSSProperties } from 'react';
import { Link } from '@react-email/components';
import { FooterSection } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useContentComposer } from '../../content-composer/content-composer-provider';
import { useComposerSection } from '../../content-composer/hooks/use-composer-section';
import type { ComposerSectionOutputProps, FooterLinkType, FrameAlignment } from '../../content-composer/types';
import { EmailSvg } from '../assets/email-svg';
import { FacebookSvg } from '../assets/facebook-svg';
import { GoogleSvg } from '../assets/google-svg';
import { PhoneSvg } from '../assets/phone-svg';
import { EmailTable } from '../atoms/email-table';
import { SectionFrame } from '../atoms/section-frame';
import { useLinks } from '../hooks/use-links';
import { getAlignment } from '../utils/frame-utils';

export const FooterOutput = ({ renderMode }: ComposerSectionOutputProps) => {
  const { t } = useTranslation('content-composer');
  const { props } = useComposerSection<FooterSection>();
  const { composerMode } = useContentComposer();

  const { formatLink, getAddress, getBusinessName, getManagementUrl, getOneClickUrl } = useLinks();

  const alignment = getAlignment(props.frame?.alignment);

  const textStyle = {
    color: props.textColor,
    fontSize: props.textSize,
    fontStyle: props.italic ? 'italic' : 'normal',
    fontFamily: props.fontType,
    fontWeight: props.bold ? theme.font.weight.bold : theme.font.weight.regular,
  };

  return (
    <SectionFrame {...props.frame}>
      <EmailTable>
        {props?.links && props.links.length > 0 && (
          <Line alignment={alignment} style={{ paddingBottom: theme.spacing(4) }}>
            {props.links.map((link, index, arr) => {
              const marginRight = index === arr.length - 1 ? 0 : theme.spacing(1);
              return (
                <Link
                  key={link.url + '-' + index}
                  href={formatLink(link.url, link.linkType as FooterLinkType, renderMode)}
                  style={{
                    ...textStyle,
                    verticalAlign: link.linkType === 'custom' ? 'super' : 'initial',
                    marginRight,
                    width: link.linkType !== 'custom' ? theme.spacing(3) : 'auto',
                    height: link.linkType !== 'custom' ? theme.spacing(3) : 'auto',
                  }}
                >
                  {link.linkType === 'custom' && link?.text}
                  {link.linkType === 'email' && <EmailSvg fillColor={props.textColor} />}
                  {link.linkType === 'phone' && <PhoneSvg fillColor={props.textColor} />}
                  {link.linkType === 'facebook' && <FacebookSvg fillColor={props.textColor} />}
                  {link.linkType === 'google' && <GoogleSvg fillColor={props.textColor} />}
                </Link>
              );
            })}
          </Line>
        )}
        <Line alignment={alignment} style={textStyle}>
          {getAddress(renderMode)}
        </Line>
        <Line alignment={alignment} style={{ ...textStyle, paddingBottom: theme.spacing(4) }}>
          {getBusinessName(renderMode)}
        </Line>
        {composerMode !== 'feedback' && (
          <Line alignment={alignment}>
            <EmailTable alignment={alignment} fullWidth={false} style={{ display: 'inline-table' }}>
              <EmailTable.Row>
                <EmailTable.Cell alignment={alignment}>
                  <Link href={getManagementUrl(renderMode)} style={textStyle}>
                    {t('Preference Center')}
                  </Link>
                </EmailTable.Cell>
                <EmailTable.Cell
                  alignment={alignment}
                  style={{ ...textStyle, fontSize: '14px', padding: theme.spacing(0, 1), color: props.textColor }}
                >
                  |
                </EmailTable.Cell>
                <EmailTable.Cell alignment={alignment}>
                  <Link href={getOneClickUrl(renderMode)} style={textStyle}>
                    {t('Unsubscribe')}
                  </Link>
                </EmailTable.Cell>
              </EmailTable.Row>
            </EmailTable>
          </Line>
        )}
      </EmailTable>
    </SectionFrame>
  );
};

type LineProps = {
  alignment?: FrameAlignment;
  children: React.ReactNode;
  style?: CSSProperties;
};

const Line = ({ alignment = 'center', children, style }: LineProps) => (
  <EmailTable.Row>
    <EmailTable.Cell alignment={alignment} style={{ paddingBottom: theme.spacing(1), ...style }}>
      {children}
    </EmailTable.Cell>
  </EmailTable.Row>
);
